/**
 * focusTrap.ts — keep Tab inside a modal surface, and give focus back to
 * whatever opened it.
 *
 * The webapp had no focus-trap utility (the New Deal sheet recon, 2026-10-06:
 * 0 files). A `role="dialog"` with `aria-modal="true"` promises assistive
 * technology that nothing outside it is reachable; without a trap, Tab walks
 * straight out of the sheet into the page behind the scrim, and the promise is
 * false. This is the smallest thing that keeps it:
 *
 *   - Tab on the LAST focusable element wraps to the first; Shift+Tab on the
 *     first wraps to the last. Focus that has escaped the container (a click
 *     on the scrim, a portal) is pulled back in on the next Tab.
 *   - On unmount, focus returns to the element that held it at mount — the
 *     opener — if that element is still in the document.
 *
 * Initial focus is the CALLER's: the sheet's first field carries autoFocus,
 * which runs before this effect, so the opener is captured from the first
 * render's activeElement, not from the effect.
 */
import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/** The tabbable elements inside `root`, in document order. Hidden and
 *  disabled controls are excluded by the selector; `tabindex="-1"` is
 *  reachable by script but not by Tab, so it is excluded too. */
export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
    .filter(el => el.getAttribute('aria-hidden') !== 'true')
}

export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, enabled = true) {
  // Captured during the FIRST render, before any child's autoFocus moves it.
  const opener = useRef<Element | null>(typeof document !== 'undefined' ? document.activeElement : null)

  useEffect(() => {
    if (!enabled) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return
      const root = containerRef.current
      if (!root) return
      const items = focusableWithin(root)
      if (items.length === 0) { e.preventDefault(); return }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement
      const inside = active instanceof Node && root.contains(active)
      if (e.shiftKey) {
        if (!inside || active === first) { e.preventDefault(); last.focus() }
      } else if (!inside || active === last) {
        e.preventDefault(); first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [containerRef, enabled])

  useEffect(() => {
    const back = opener.current
    return () => {
      if (back instanceof HTMLElement && back.isConnected) back.focus()
    }
  }, [])
}
